import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * AdminShell — left-rail layout for the platform-admin cockpit.
 *
 * Renders inside AppShell. The top app nav stays in place so admins can
 * jump back into owner workflows; this just adds a sidebar inside the
 * page <main>.
 */
export function AdminShell({ children }: { children: ReactNode }) {
  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex flex-col md:flex-row gap-6">
        <aside aria-label="Admin sections" className="md:w-56 flex-shrink-0">
          <nav className="rounded-lg border border-border bg-card p-2 flex md:flex-col gap-1 overflow-x-auto">
            <Item to="/admin" end>Overview</Item>
            <Item to="/admin/orgs">Organizations</Item>
            <Item to="/admin/subscriptions">Subscriptions</Item>
            <Item to="/admin/pricing">Pricing catalog</Item>
            <Item to="/admin/promotions">Promotions</Item>
            <Item to="/admin/banners">Banners</Item>
            <Item to="/admin/audit">Audit feed</Item>
            <Item to="/admin/settings">Settings</Item>
          </nav>
          <p className="mt-2 px-2 text-[11px] text-muted-foreground">
            Platform admin only. All actions are audit-logged.
          </p>
        </aside>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

function Item({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'rounded-md px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 whitespace-nowrap',
          isActive
            ? 'bg-muted/60 font-medium'
            : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
        )
      }
      style={({ isActive }) =>
        isActive ? { color: 'hsl(var(--brand-navy))' } : undefined
      }
    >
      {children}
    </NavLink>
  );
}
